import {
  FirestoreDataType,
  LanguageString,
  LearningLibraryTranslation,
  FirestoreObject,
  firestoreDataTypes,
  Translation,
  FirestoreObjectMap,
  EnvString,
  CustomKeyType,
} from '@sworkit/api';
import { ref, set } from 'firebase/database';
import {
  collection,
  deleteDoc,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  WhereFilterOp,
} from 'firebase/firestore';
import { swApi } from './app';
import { firebaseFunctions, fst as firestore, realtimeDatabase } from './firebase';
import { removeUndefinedAndNaNRecursive } from './utils';
import { httpsCallable } from 'firebase/functions';

export const envKeyMap = {
  prod: 'isProd',
  alpha: 'isAlpha',
  beta: 'isBeta',
};

export const envNameMap = {
  prod: 'Prod',
  alpha: 'Alpha',
  beta: 'Beta'
};

export async function saveFirestoreData(
  dataType: FirestoreDataType,
  dataObject: FirestoreObject,
) {
  if (!firestore || !dataObject) return;
  if (!dataObject.id) {
    const docRef = doc(collection(firestore, dataType));
    dataObject.id = docRef.id;
  }
  await setDoc(
    doc(firestore, `/${dataType}/${dataObject.id}`),
    removeUndefinedAndNaNRecursive(dataObject),
    { merge: true },
  );
  return dataObject;
}

export async function getFirestoreData<T>(
  dataType: FirestoreDataType,
  id: string,
) {
  if (!firestore || !id || !dataType) return;
  const dataObject = await getDoc(doc(firestore, `/${dataType}/${id}`));
  const document = { ...dataObject.data(), id: dataObject.id };
  return document as T;
}

export async function queryFirestoreData<T>(
  dataType: FirestoreDataType,
  filters: [string | FieldPath, WhereFilterOp, any][] = [],
) {
  if (!firestore || !dataType) return;
  let q = query(collection(firestore, dataType));
  if (filters?.length) {
    q = query(
      q,
      ...filters.map((filter) => where(filter[0], filter[1], filter[2])),
    );
  }

  const dataObjects = await getDocs(q);
  const documents = dataObjects.docs.map((obj) => ({
    ...obj.data(),
    id: obj.id,
    dataType,
  }));
  return documents[0] as T;
}

export async function deleteFirestoreData(
  dataType: FirestoreDataType,
  dataObject: Partial<FirestoreObject>,
) {
  if (!firestore) return;
  if (!dataObject?.id) return;
  await deleteDoc(doc(firestore, `/${dataType}/${dataObject.id}`));
  return dataObject;
}

export async function updateRealtimeDatabaseVersion(
  dataType: FirestoreDataType,
  newVersions: any,
) {
  if (!firestore || !swApi?.env) return;
  return set(ref(realtimeDatabase, `${dataType}DataVersions/`), newVersions);
}

export async function buildNewData(
  customer: CustomKeyType,
  version: number,
  dataType: FirestoreDataType,
  env: EnvString
) {
  if (!firestore || !swApi?.env || !customer) {
    console.error('error, not deploying');
    return;
  }
  const buildData =   httpsCallable<
  {
    customer: CustomKeyType;
    version: number;
    dataType: FirestoreDataType;
    env: EnvString
  },
  {
    successPath: string
  }
>(firebaseFunctions, 'buildData');
return buildData({
  customer, version, dataType, env
})
}

export async function updateRealTimeDatabaseCustomerVersion(
  customer: string,
  version: number,
) {
  if (!firestore || !swApi?.env || !customer) {
    console.error('error, not deploying');
    return;
  }
  return set(
    ref(realtimeDatabase, `customerDataVersions/${customer}`),
    version,
  );
}

export async function saveLearningLibraryTranslation(
  learningLibraryId: string,
  language: LanguageString,
  dataObject: Partial<LearningLibraryTranslation>,
) {
  if (!firestore || !dataObject) return;
  await setDoc(
    doc(
      firestore,
      `learningLibrary/${learningLibraryId}/translations/${language}`,
    ),
    removeUndefinedAndNaNRecursive(dataObject),
    { merge: true },
  );
  return dataObject;
}

export async function saveFirestoreTranslation<T>(
  firestoreDataId: string,
  language: LanguageString,
  dataObject: Partial<Translation<T>>,
  firestoreDataType: FirestoreDataType,
) {
  if (!firestore || !dataObject) return;
  await setDoc(
    doc(
      firestore,
      `${firestoreDataType}/${firestoreDataId}/translations/${language}`,
    ),
    removeUndefinedAndNaNRecursive(dataObject),
    { merge: true },
  );
  return dataObject;
}

export function listenToFirestoreData<T extends FirestoreObject>(
  firestoreDataKey: FirestoreDataType,
  callback: (firestoreData: T[]) => void,
) {
  const q = query(collection(firestore, firestoreDataKey));
  return onSnapshot(q, (snap) => {
    const all = snap.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id, dataType: firestoreDataKey } as T),
    );
    callback(all);
  });
}

export async function findAFirestoreItemWithoutDataType(
  filters: [string | FieldPath, WhereFilterOp, any],
) {
  for (const collectionName of firestoreDataTypes) {
    const found = await findAFirestoreItemWithDataType(filters, collectionName);
    console.log({ found });
    if (found) return found;
  }
  return null;
}

export async function findAFirestoreItemWithDataType(
  filters: [string | FieldPath, WhereFilterOp, any],
  collectionName: FirestoreDataType,
) {
  console.log('searching', collectionName, filters);
  try {
    type K = typeof collectionName;
    type T = FirestoreObjectMap[K];
    const result = await queryFirestoreData<T>(collectionName, [filters]);
    if (result) return result;
  } catch (e) {
    console.error(e);
  }

  return null;
}
